import { createAuthenticationService } from 'commercial-web-client-utilities/dist/composables/authentication-service';
import { createCommwebService } from 'commercial-web-client-utilities/dist/composables/commweb-service';
import { createLedsService } from 'commercial-web-client-utilities/dist/composables/leds-service';
import { createMonitoringService } from 'commercial-web-client-utilities/dist/composables/monitoring-service';
import { createNonsecureService } from 'commercial-web-client-utilities/dist/composables/nonsecure-service';
import {
    defaultCommwebServiceBaseUrl,
    defaultLegacyCommwebServiceBaseUrl,
    defaultLedsServiceBaseUrl,
    ledsClientId,
    defaultAemServiceBaseUrl
} from '@/config/service-constants';

import type { AxiosResponse } from 'axios';
import type { NonsecureService } from 'commercial-web-client-utilities/dist/composables/nonsecure-service';
import type { SiteConfiguration } from '@/types';

// Create service plugin for authentication with commweb and LEDS services.
const authenticationService = createAuthenticationService({
    legacyCommwebServiceBaseUrl: defaultLegacyCommwebServiceBaseUrl,
    commwebServiceBaseUrl: defaultCommwebServiceBaseUrl,
    ledsServiceBaseUrl: defaultLedsServiceBaseUrl,
    ledsClientId
});

// Create service plugin for making secure commweb service calls.
const commwebService = createCommwebService({
    authenticationService,
    baseUrl: defaultCommwebServiceBaseUrl
});

// Create service plugin for making secure LEDS service calls.
const ledsService = createLedsService({
    authenticationService,
    baseUrl: defaultLedsServiceBaseUrl,
    clientId: ledsClientId
});

// Create service plugin for logging events and data to analytics + monitoring.
const monitoringService = createMonitoringService({
    commwebService
});

// Create service plugin for making non-secured calls (anything that doesn't require login)
const nonsecureService = createNonsecureService();

interface AppManagementService {
    getSiteConfiguration(options: { branding: string }): Promise<AxiosResponse<SiteConfiguration>>;
}


interface CreateAppManagementServiceOptions {
    nonsecureService: NonsecureService;
}

/**
 * Contains ease of use functions for making service calls that pertain to setting up the app
 * session and initial data retrieval.
 */
function createAppManagementService(options: CreateAppManagementServiceOptions): AppManagementService {
    const {
        nonsecureService
    } = options;

    const nonsecureClient = nonsecureService.client;

    /**
     * Retrieves general site information for the specified LOB (identified by branding code).
     * This API does not require authentication to be called.
     */
    async function getSiteConfiguration(): Promise<AxiosResponse<SiteConfiguration>> {
        return await nonsecureClient.get(
            defaultAemServiceBaseUrl + 'bin/commercial-web-public/site-config.json'
        );
    }

    return {
        getSiteConfiguration
    };
}

export {
    authenticationService, commwebService, ledsService, monitoringService, nonsecureService,
    createAppManagementService, CreateAppManagementServiceOptions
};
